import { Attribute } from "./attribute";
import { Offer } from "./offer";
import { CategoryPicture, LightPicture, Picture } from "./picture";
import { Page } from "@/models/page";

export class Product {
  id?: string;
  reference?: string;
  slug?: string;
  name?: string;
  description?: string;
  gallery?: Picture[];
  lightGallery?: [];
  pictureCover?: LightPicture | "";
  categoryPaths?: string[];
  attributeValuesByAttributeName?: Record<string, Attribute>;
  descriptionShort?: Attribute;
  descriptionLong?: Attribute;
  focus?: FocusMarketing[] | any;
  relatedProduct?: Attribute | undefined;
  children?: Product[];
  configurableAttributes?: any;
  offers?: Offer[];
  percentPromo?: number;
  stockQuantity?: number;
  taxIncludedPrice?: number | null;
  taxFreePrice?: number | null;
  discountPrice?: number | null;
  minimalOrderQuantity?: number;
  maximalOrderQuantity?: number;
  isNew?: Attribute | boolean;
  seoTitle?: Attribute | null;
  seoDescription?: Attribute | null;
  defaultOfferId?: string;
  notices?: Notices[];
  brand?: Attribute;
  restock_date?: Attribute;
  private defaultOffer?: Offer;
  practice?:Attribute;
  plus?:Attribute;
  information?:Attribute;


  public static of(dto: any): Product {
    const p: Product = new Product();
    p.id = dto.id;
    p.reference = dto.reference;
    p.slug = dto.slug;
    p.name = dto.name;
    p.categoryPaths = dto.categoryPaths;

    Object.keys(dto.attributeValuesByAttributeName).forEach(function (key, index) {
      dto.attributeValuesByAttributeName[key].index = index;
      dto.attributeValuesByAttributeName[key] = Attribute.of(dto.attributeValuesByAttributeName[key]);
    });

    p.attributeValuesByAttributeName = dto.attributeValuesByAttributeName;
    p.description = dto.description;
    p.configurableAttributes = dto?.configurableAttributes;
    p.defaultOfferId = dto?.defaultOfferId;

    // Set description short & long and delete reference in attributes list
    p.descriptionShort = p.attributeValuesByAttributeName?.["description_short"];
    delete p?.attributeValuesByAttributeName?.["description_short"];
    p.descriptionLong = p.attributeValuesByAttributeName?.["description_long"];
    delete p?.attributeValuesByAttributeName?.["description_long"];
    p.relatedProduct = p.attributeValuesByAttributeName?.["product_associated"];
    delete p?.attributeValuesByAttributeName?.["product_associated"];
// remove shipping attribute
    delete p?.attributeValuesByAttributeName?.["logistic_transport_width"];
    delete p?.attributeValuesByAttributeName?.["logistic_transport_height"];
    delete p?.attributeValuesByAttributeName?.["logistic_transport_length"];
    delete p?.attributeValuesByAttributeName?.["logistic_transport_weight"];
    //remove ean  / logisticClass / taille / entretient
    delete p?.attributeValuesByAttributeName?.["ean"];
    delete p?.attributeValuesByAttributeName?.["product_logistic_class"];

    p.restock_date = p.attributeValuesByAttributeName?.["product_message_expedition"];
    delete p?.attributeValuesByAttributeName?.["product_message_expedition"];
    p.isNew = p?.attributeValuesByAttributeName?.["marketing_new"] || false;

    p.seoTitle = p?.attributeValuesByAttributeName?.["product_seo_title"] || null;
    delete p?.attributeValuesByAttributeName?.["product_seo_title"];
    p.seoDescription = p?.attributeValuesByAttributeName?.["product_seo_description"] || null;
    delete p?.attributeValuesByAttributeName?.["product_seo_description"];
    delete p?.attributeValuesByAttributeName?.["marketing_new"];

    p.brand = p?.attributeValuesByAttributeName?.["details_brand"];
    p.practice = p?.attributeValuesByAttributeName?.["product_in_practice"];
    delete p?.attributeValuesByAttributeName?.["product_in_practice"];

    p.information = p?.attributeValuesByAttributeName?.["product_info"];
    delete p?.attributeValuesByAttributeName?.["product_info"];

    p.plus =  p?.attributeValuesByAttributeName?.["product_plus"];
    delete p?.attributeValuesByAttributeName?.["product_plus"];
    //remove delay
    delete p?.attributeValuesByAttributeName?.["product_picking_delay"];




    p.notices = [];
    if (dto?.attributeValuesByAttributeName?.["docs"]) {
      const tempFocus: any[] = JSON.parse(dto?.attributeValuesByAttributeName?.["docs"]?.value);
      if (tempFocus) {

        tempFocus.forEach(b => p?.notices?.push(Notices.of(b)));
      }
      delete p?.attributeValuesByAttributeName?.["docs"];
    }
    p.focus = [];
    p.offers = [];

    if (dto?.attributeValuesByAttributeName?.["marketing_focus"]) {
      const tempFocus: any[] = JSON.parse(dto?.attributeValuesByAttributeName?.["marketing_focus"]?.value);
      if (tempFocus) {
        tempFocus.forEach(b => p.focus.push(FocusMarketing.of(b)));
      }
      delete p?.attributeValuesByAttributeName?.["marketing_focus"];
    }

    if (dto?.pictures?.length > 0) {

      p.gallery = dto.pictures.map(p => {
        return Picture.of(p, dto.name);
      });
      p.lightGallery = dto.pictures.map(p => {
        return LightPicture.of(p, dto.name);
      });
      const cover = dto.pictures.find(p => p.isCover === true);
      p.pictureCover = cover && LightPicture.of(cover, dto.name);
    }

    if (dto.children && dto.children.length > 0) {
      p.children = [];
      dto.children.forEach((child: Product) => {
        p.children?.push(Product.of(child));
        if (!p.defaultOffer) {
          p.defaultOffer = child?.offers?.find(o => o.id === dto?.defaultOfferId);
        }
      });
    } else if (dto.offers && dto.offers.length > 0) {

      dto.offers.forEach((offer: Offer) => {
        p.offers?.push(Offer.of(offer));
      });
      if (dto?.defaultOfferId && !p.defaultOffer) {
        p.defaultOffer = Offer.of(dto.offers.find(o => o.id === dto?.defaultOfferId));
      }
    }

    p.stockQuantity = p?.defaultOffer?.stockQuantity || 0;
    p.taxIncludedPrice = p?.defaultOffer?.price?.taxIncluded;
    p.taxFreePrice = p?.defaultOffer?.price?.taxFree;
    p.discountPrice = p?.defaultOffer?.discountPrice?.taxIncluded;
    if (p.discountPrice && p.taxIncludedPrice) {
      p.percentPromo = ((p.discountPrice - p.taxIncludedPrice) / p.taxIncludedPrice);
    }
    p.minimalOrderQuantity = p?.defaultOffer?.minimalOrderQuantity;
    p.maximalOrderQuantity = p?.defaultOffer?.maximalOrderQuantity;

    return p;
  }
}

export class FocusMarketing {
  title?: { [key: string]: string };
  description?: { [key: string]: string };
  image?: any;

  static of(data: any) {
    const f: FocusMarketing = new FocusMarketing();

    f.description = data.product_marketing_focus_description || "";
    f.title = data.product_marketing_focus_title || "";
    f.image = data.product_marketing_focus_image ? {
      src: data.product_marketing_focus_image[0]?.url || "",
      alt: data.product_marketing_focus_image[0]?.alt || "",
    } : null;

    return f;
  }
}

export class Notices {
  name?: string;
  url?: string;

  static of(data: any) {
    const n: Notices = new Notices();

    n.name = data.name;
    n.url = data.url;

    return n;
  }
}

export class LightProduct {
  defaultOfferId?: string;
  percentPromo?: number;
  defaultChildSlug?: string;
  price?: number;
  discountPrice?: number;
  stockQuantity?: number;
  reference?: string;
  slug?: string;
  brand?: string;
  name?: string;
  description?: string;
  pictureCover?: CategoryPicture;
  categoryPaths?: string[];
  isConfigurable?: boolean;

  public static of(data: any): LightProduct {
    const l: LightProduct = new LightProduct();
    l.defaultOfferId = data?.defaultOfferId;
    l.defaultChildSlug = data?.defaultChildSlug;
    l.price = data?.price;
    l.brand = data?.brand;
    l.discountPrice = data?.discountPrice || null;
    l.stockQuantity = data?.stockQuantity || null;
    l.reference = data?.reference;
    l.slug = data?.slug;
    l.name = data?.name;
    if (data?.discountPrice) {
      l.percentPromo = ((data?.discountPrice - data?.price) / data?.price);
    }
    l.description = data?.description;
    l.categoryPaths = data?.categoryPaths;
    l.isConfigurable = data?.isConfigurable;
    l.pictureCover = data?.pictures.filter(picture => (picture.isCover === true)).map(picture => picture ? CategoryPicture.of(picture, data?.name) : CategoryPicture.of([], data?.name))[0] || "";
    return l;
  }
}

export class ProductsWithFilters {
  products?: Page<LightProduct>;
  filterableAttributes?: unknown;
  currentFilterableAttributes?: unknown;
}